import dayjs, { Dayjs } from 'dayjs';

import { AchAuthorizationAcceptance, AchAuthorizationSettings } from '../types';

type BuildAchAuthorizationInput = AchAuthorizationSettings & {
	recurring: boolean;
	accountType: string;
	amountInCents: number;
	paymentDueDay?: number | null;
	now?: Dayjs;
};

export function getNextRecurringPaymentDate(paymentDueDay?: number | null, now = dayjs()) {
	const today = now.startOf('day');
	const dayOfMonth = Number(paymentDueDay) || 1;
	const dateInMonth = (month: Dayjs) => month.date(Math.min(dayOfMonth, month.daysInMonth()));
	const thisMonth = dateInMonth(today);

	return thisMonth.isAfter(today, 'day') ? thisMonth : dateInMonth(today.add(1, 'month').startOf('month'));
}

export function buildAchAuthorization({
	recurring,
	operatorLegalName,
	operatorAccountLast4,
	operatorAbaLast4,
	accountType,
	amountInCents,
	paymentDueDay,
	cancellationMethod,
	revocationNoticeDays,
	now = dayjs(),
}: BuildAchAuthorizationInput): AchAuthorizationAcceptance {
	const legalName = operatorLegalName || 'the parking operator';
	const identifiers = [
		operatorAccountLast4 && `Account ID ${operatorAccountLast4}`,
		operatorAbaLast4 && `ABA ${operatorAbaLast4}`,
	].filter(Boolean);
	const operatorDescription = `${legalName}${identifiers.length ? ` (${identifiers.join(', ')})` : ''}`;
	const normalizedAccountType = accountType.toLowerCase();
	const effectiveDate = recurring ? getNextRecurringPaymentDate(paymentDueDay, now) : now.startOf('day');
	const formattedEffectiveDate = effectiveDate.format('M/D/YYYY');
	const amountDescription =
		recurring && amountInCents <= 0
			? 'the amount shown on each monthly invoice'
			: `$${(amountInCents / 100).toFixed(2)}`;
	const cancelBy = cancellationMethod || `contacting ${legalName}`;
	const noticeDays = revocationNoticeDays ?? 5;
	const authorizationText = recurring
		? `This ACH transaction is governed by NACHA rules and regulations. I authorize ${operatorDescription} to electronically debit my ${normalizedAccountType} account identified above, beginning ${formattedEffectiveDate} and recurring each month. This authorization will remain in effect until I revoke it by ${cancelBy}. I understand that to revoke this authorization, ${legalName} must receive notice at least ${noticeDays} business days before the next scheduled debit to prevent that debit. If necessary, I also authorize ${legalName} to electronically credit my account to correct an erroneous debit.`
		: `This ACH transaction is governed by NACHA rules and regulations. I authorize ${operatorDescription} to electronically debit my ${normalizedAccountType} account identified above for a one-time payment of ${amountDescription} on or after ${formattedEffectiveDate}. This authorization applies only to this payment and does not authorize recurring payments.`;

	return {
		authorizationType: recurring ? 'Recurring' : 'One_Time',
		authorizationText,
		accountType: normalizedAccountType,
		amountInCents,
		effectiveDate: effectiveDate.format('YYYY-MM-DD'),
		recurrenceFrequency: recurring ? 'Monthly' : null,
	};
}
